<template>
  <v-app>
    <!-- Navigation Drawer (Sidebar) - Only show when authenticated -->
    <v-navigation-drawer
      v-if="authStore.isAuthenticated"
      v-model="drawer"
      :rail="rail"
      permanent
      class="nav-drawer"
    >
      <!-- Logo/Brand Section -->
      <div class="brand-section pa-4" :class="{ 'pa-2': rail }">
        <div class="d-flex align-center" :class="{ 'justify-center': rail }">
          <v-avatar color="primary" size="40" class="brand-avatar">
            <span class="text-h6 font-weight-bold">LT</span>
          </v-avatar>
          <div v-if="!rail" class="ml-3">
            <div class="text-subtitle-1 font-weight-bold">Lala Tacos</div>
            <div class="text-caption text-medium-emphasis">Admin Panel</div>
          </div>
        </div>
      </div>

      <v-divider class="my-2"></v-divider>

      <!-- Navigation Items -->
      <v-list nav density="comfortable" class="px-2">
        <v-list-item
          v-for="item in navItems"
          :key="item.title"
          :to="item.to"
          :prepend-icon="item.icon"
          :title="item.title"
          rounded="lg"
          class="mb-1 nav-item"
          :class="{ 'nav-item-active': isActiveRoute(item.to) }"
        >
          <template v-slot:append>
            <v-chip
              v-if="item.wip"
              color="error"
              size="small"
              label
              pill
            >
              WIP
            </v-chip>
          </template>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <v-divider class="mb-2"></v-divider>
        <v-list nav density="comfortable" class="px-2 pb-2">
          <v-list-item
            prepend-icon="mdi-logout"
            title="Logout"
            rounded="lg"
            class="nav-item logout-item"
            @click="logout"
          >
          </v-list-item>
        </v-list>

        <!-- Rail Toggle -->
        <div class="d-flex justify-center pb-3">
          <v-btn
            :icon="rail ? 'mdi-chevron-right' : 'mdi-chevron-left'"
            variant="text"
            size="small"
            @click="rail = !rail"
          ></v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- App Bar - Only show when authenticated -->
    <v-app-bar
      v-if="authStore.isAuthenticated"
      flat
      class="app-bar px-4"
      color="transparent"
    >
      <v-app-bar-nav-icon
        class="d-lg-none"
        @click="drawer = !drawer"
      ></v-app-bar-nav-icon>

      <v-toolbar-title class="text-h6 font-weight-medium">
        {{ currentPageTitle }}
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <!-- Theme toggle -->
      <v-btn icon variant="text" class="mr-2" @click="toggleTheme">
        <v-icon>{{ isDark ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
      </v-btn>

      <v-menu>
        <template v-slot:activator="{ props }">
          <v-btn v-bind="props" variant="text" class="text-none">
            <v-avatar color="primary" size="32" class="mr-2">
              <span class="text-caption font-weight-bold">{{ userInitials }}</span>
            </v-avatar>
            <span class="d-none d-sm-inline">{{ authStore.user?.username || 'Admin' }}</span>
            <v-icon end>mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list density="compact" min-width="150">
          <v-list-item prepend-icon="mdi-logout" title="Logout" @click="logout"></v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <!-- Main Content -->
    <v-main :class="{ 'main-content': authStore.isAuthenticated, 'main-content-login': !authStore.isAuthenticated }">
      <router-view />
    </v-main>

    <!-- Global notification snackbar -->
    <v-snackbar
      v-model="notificationStore.snackbar.show"
      :color="notificationStore.snackbar.color"
      :timeout="notificationStore.snackbar.timeout"
      location="bottom right"
      rounded="lg"
      elevation="4"
    >
      <div class="d-flex align-center">
        <v-icon
          :icon="getSnackbarIcon(notificationStore.snackbar.color)"
          class="mr-2"
        ></v-icon>
        {{ notificationStore.snackbar.message }}
      </div>
      <template v-slot:actions>
        <v-btn
          variant="text"
          size="small"
          @click="notificationStore.hideSnackbar()"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import { useAuthStore, useNotificationStore } from "./stores";
import { ref, computed } from "vue";
import { useRoute } from "vue-router";
import { useTheme } from "vuetify";

export default {
  name: "App",
  setup() {
    const authStore = useAuthStore();
    const notificationStore = useNotificationStore();
    const route = useRoute();
    const theme = useTheme();
    const isDark = computed(() => theme.global.current.value.dark);
    const drawer = ref(true);
    const rail = ref(false);

    const navItems = [
      { title: "Dashboard", icon: "mdi-view-dashboard-outline", to: "/dashboard" },
      { title: "Quotes", icon: "mdi-file-document-outline", to: "/quotes" },
      { title: "Customers", icon: "mdi-account-group-outline", to: "/customers" },
      { title: "Online Orders", icon: "mdi-cart-check", to: "/online-orders" },
      { title: "Comeback Offers", icon: "mdi-tag-heart-outline", to: "/comeback-offers" },
      { title: "Order Totals", icon: "mdi-file-table-outline", to: "/order-totals" },
      { title: "Manage Menus", icon: "mdi-silverware-fork-knife", to: "/menus", wip: true },
      { title: "Manage Proteins", icon: "mdi-food-steak", to: "/proteins", wip: true },
      { title: "Manage Addons", icon: "mdi-plus-box-outline", to: "/addons", wip: true },
      { title: "Addon Packages", icon: "mdi-package-variant", to: "/addon-packages", wip: true },
      { title: "Statistics", icon: "mdi-chart-box-outline", to: "/statistics" },
      { title: "Users", icon: "mdi-account-cog-outline", to: "/users" },
      { title: "API Docs", icon: "mdi-api", to: "/docs" },
    ];

    const userInitials = computed(() => {
      const name = authStore.user?.username || '';
      return name.slice(0, 2).toUpperCase() || 'AD';
    });

    const currentPageTitle = computed(() => {
      const item = navItems.find(item => route.path.startsWith(item.to));
      return item?.title || "Dashboard";
    });

    const isActiveRoute = (to) => {
      return route.path === to || route.path.startsWith(to + '/');
    };

    const getSnackbarIcon = (color) => {
      const icons = {
        success: 'mdi-check-circle',
        error: 'mdi-alert-circle',
        warning: 'mdi-alert',
        info: 'mdi-information',
      };
      return icons[color] || 'mdi-information';
    };

    const toggleTheme = () => {
      theme.global.name.value = isDark.value ? 'light' : 'dark';
    };

    return {
      authStore,
      notificationStore,
      drawer,
      rail,
      navItems,
      currentPageTitle,
      isActiveRoute,
      getSnackbarIcon,
      isDark,
      toggleTheme,
      userInitials,
    };
  },
  methods: {
    logout() {
      this.authStore.logout();
      this.$router.push("/");
    },
  },
};
</script>

<style>
/* Global Styles */
* {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
}

html {
  overflow-y: auto !important;
}

body {
  background-color: rgb(var(--v-theme-background));
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Navigation Drawer Styles */
.nav-drawer {
  background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%) !important;
  border-right: none !important;
}

.nav-drawer .v-list-item {
  color: rgba(255, 255, 255, 0.7) !important;
  margin-bottom: 4px;
}

.nav-drawer .v-list-item:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
  color: #fff !important;
}

.nav-drawer .nav-item-active {
  background-color: rgba(99, 102, 241, 0.2) !important;
  color: #a5b4fc !important;
}

.nav-drawer .nav-item-active .v-list-item__prepend .v-icon {
  color: #a5b4fc !important;
}

.nav-drawer .logout-item:hover {
  background-color: rgba(239, 68, 68, 0.15) !important;
  color: #fca5a5 !important;
}

.brand-section {
  background: transparent;
}

.brand-avatar {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%) !important;
}

/* App Bar Styles */
.app-bar {
  background: rgba(var(--v-theme-background), 0.8) !important;
  backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(var(--v-theme-on-background), 0.12) !important;
}

/* Main Content */
.main-content {
  background-color: rgb(var(--v-theme-background));
  min-height: 100vh;
}

.main-content-login {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
}

/* Global Card Improvements */
.v-card {
  border-color: rgba(var(--v-theme-on-background), 0.12) !important;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
}

/* Data Table Improvements */
.v-data-table {
  border-radius: 12px !important;
  overflow: hidden;
}

.v-data-table .v-data-table-header {
  background-color: rgb(var(--v-theme-surface-variant)) !important;
}

.v-data-table .v-data-table__th {
  font-weight: 600 !important;
  color: rgb(var(--v-theme-secondary)) !important;
  font-size: 0.75rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  border-bottom: 2px solid rgba(var(--v-theme-on-background), 0.12) !important;
  text-align: left !important;
}

.v-data-table .v-data-table__td {
  border-bottom: 1px solid rgba(var(--v-theme-on-background), 0.06) !important;
  text-align: left !important;
}

.v-data-table .v-data-table__th:last-child,
.v-data-table .v-data-table__td:last-child {
  text-align: center !important;
}

.v-data-table tbody tr:hover {
  background-color: rgb(var(--v-theme-surface-variant)) !important;
}

/* Tooltips: Vuetify draws them in surface-variant, which the theme sets to a
   light grey for table headers, leaving light text on a light box. */
.v-tooltip > .v-overlay__content {
  background: #1e293b !important;
  color: #f8fafc !important;
  font-size: 0.8125rem !important;
  line-height: 1.4 !important;
  padding: 6px 10px !important;
  border-radius: 6px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Chip Improvements */
.v-chip {
  font-weight: 500 !important;
}

/* Button Improvements */
.v-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
}

/* Dialog Improvements */
.v-dialog .v-card {
  border-radius: 16px !important;
}

.v-dialog .v-card-title {
  font-size: 1.25rem !important;
  font-weight: 600 !important;
  padding: 20px 24px 12px !important;
}

/* Input Improvements */
.v-text-field .v-field {
  border-radius: 8px !important;
}

/* Page Title Styles */
.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-background));
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 0.875rem;
  color: rgb(var(--v-theme-secondary));
}

/* Stat Card Styles */
.stat-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08) !important;
}

/* Alert/Status Colors */
.status-success { color: #10b981; }
.status-warning { color: #f59e0b; }
.status-error { color: #ef4444; }
.status-info { color: #3b82f6; }

/* Scrollbar Styling */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: rgba(var(--v-theme-on-background), 0.25);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--v-theme-on-background), 0.4);
}
</style>
